import React, { useContext, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import map from 'lodash/map';
import uniqueId from 'lodash/uniqueId';
import { BreakpointContext } from '~/helpers/BreakpointDetector/BreakpointContext';
import { mq } from '~/styles/grid';
import Picture from '../../atoms/Picture';
import getImageProperties from '~/helpers/getImageProperties';
import { getClientConfig } from '../../../config/getClientConfig';
import CTA from '~/components/atoms/CTA';
import { CTATag } from '~/components/atoms/CTA/CTA.constants';
import { useIntl } from 'react-intl';
import { isNarrow } from './Hero.helpers';

const { theme: defaultTheme } = getClientConfig();

const BgContainer = styled.div`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;

  img {
    width: 100%;
    height: auto;
  }

  max-width: ${(props) =>
    props.$teaserFullWidth ? '100%' : props.theme.hero.bgImageMaxWidth.xxl};
  ${(props) => css`
    @media (${mq.xs}) {
      height: auto;
      width: 100%;
      object-fit: cover;
      max-width: ${props.theme.hero.bgImageMaxWidth.sm};
    }

    ${props.$context === 'HERO' &&
    css`
      height: ${(props) =>
        props.$teaserFullWidth
          ? props.theme.teaserFullWidth.height.xxl
          : props.theme.hero.bgImageHeight.xxl};
    `};

    ${isNarrow(props.$orientation) &&
    css`
      max-width: 1920px;
      height: auto;
    `}
  `}
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledVideo = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const VideoControlButton = styled(CTA)`
  position: absolute;
  bottom: 8px;
  right: 8px;
`;

/**
 * @param {string} srcValue
 * @param {string} srcType
 * @returns {React.ReactNode}
 */
export const renderSource = (srcValue, srcType) => (
  <source
    data-src={srcValue}
    type={`video/${srcType}`}
    src={srcValue}
    key={uniqueId(srcType)}
  />
);

/**
 * @param {HeroVideoBg} background
 * @param {OrientationType} orientation
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 * @returns {React.ReactNode}
 */
const renderVideoBackground = (background, orientation, videoRef) => {
  const { poster, files } = background;
  const posterSize = isNarrow(orientation)
    ? poster + defaultTheme.imgPropertiesMapFn().wide?.xl
    : poster + defaultTheme.imgPropertiesMapFn().hero?.xl;

  return (
    <StyledVideo
      ref={videoRef}
      poster={posterSize || ''}
      autoPlay={true}
      loop={true}
      muted={true}
      playsInline={true}
      data-id="hero-background-video"
    >
      {map(files, (value, key) => renderSource(value, key))}
    </StyledVideo>
  );
};

/**
 * @param {HeroImageBg} background
 * @param {HeroImageBg} mobileImage
 * @param {boolean} startAfterSkew
 * @param {boolean} heroEVP
 * @param {'LEFT' | 'RIGHT' | 'RIGHT_NARROW' | 'LEFT_NARROW'} orientation
 * @returns {React.ReactNode}
 */
const renderImageBackground = (
  background,
  mobileImage,
  startAfterSkew,
  heroEVP,
  orientation,
) => {
  const bgImage = background;
  const mobileBgImage = mobileImage;

  return (
    <Picture
      startAfterSkew={startAfterSkew}
      urls={getImageProperties(
        {
          xxl: bgImage.url,
          xl: bgImage.url,
          lg: bgImage.url,
          md: bgImage.url,
          xs: mobileBgImage.url,
        },
        isNarrow(orientation) ? 'wide' : 'hero',
      )}
      alt={bgImage.alt || ''}
      title={bgImage.title || ''}
      heroEVP={heroEVP}
    />
  );
};

/**
 * @param {HeroBgProps} props
 * @returns {React.ReactElement} The rendered component
 */
const HeroBackground = ({
  background = {},
  mobileImage = {},
  startAfterSkew = false,
  heroEVP = false,
  teaserFullWidth = false,
  orientation,
  context,
}) => {
  const breakpoint = useContext(BreakpointContext);
  const videoRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const intl = useIntl();

  const hasVideo = 'files' in background && breakpoint !== 'xs';

  const togglePlayback = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPaused(false);
    } else {
      videoRef.current.pause();
      setIsPaused(true);
    }
  };

  return (
    <BgContainer
      $teaserFullWidth={teaserFullWidth}
      $orientation={orientation}
      $context={context}
      data-id="hero-background"
    >
      {hasVideo && (
        <VideoWrapper>
          {renderVideoBackground(background, orientation, videoRef)}
          <VideoControlButton
            skin="ICON_ONLY"
            iconBefore={isPaused ? 'PLAY' : 'PAUSE'}
            type={CTATag.Button}
            handleBtnClick={togglePlayback}
            aria-label={
              isPaused
                ? intl.formatMessage({ id: 'heroVideoPauseAriaLabelPaused' })
                : intl.formatMessage({ id: 'heroVideoPauseAriaLabelPause' })
            }
            aria-pressed={isPaused}
            data-id="hero-video-control"
          />
        </VideoWrapper>
      )}
      {!hasVideo &&
        renderImageBackground(
          background,
          mobileImage,
          startAfterSkew,
          heroEVP,
          orientation,
        )}
    </BgContainer>
  );
};

export default HeroBackground;
